// ============================================================
// decoupeZones.js — la géométrie de l'éditeur de découpe, pure.
// ------------------------------------------------------------
// Ce qui se teste sans navigateur : transformer un glisser en zone
// normalisée, nommer la zone suivante, et dire en une phrase ce que la
// découpe va faire. Les règles de validité, elles, sont dans
// `shared/decoupe.js` — les mêmes que le serveur.
// ============================================================

/** En dessous, un clic maladroit n'est pas une zone (comme chez AGC). */
export const TAILLE_MIN_PX = 12;

const borne = (v) => Math.min(1, Math.max(0, v));

/**
 * Deux points du glisser (en pixels, dans le cadre de l'image) → une zone
 * normalisée 0–1, quel que soit le sens du tracé. `null` si elle est trop
 * petite pour être voulue.
 */
export function zoneDepuisGlisser(a, b, largeur, hauteur) {
  if (!(largeur > 0) || !(hauteur > 0)) return null;
  const x0 = Math.min(a.x, b.x);
  const y0 = Math.min(a.y, b.y);
  const x1 = Math.max(a.x, b.x);
  const y1 = Math.max(a.y, b.y);
  if (x1 - x0 < TAILLE_MIN_PX || y1 - y0 < TAILLE_MIN_PX) return null;
  const x = borne(x0 / largeur);
  const y = borne(y0 / hauteur);
  return { x, y, w: borne(x1 / largeur) - x, h: borne(y1 / hauteur) - y };
}

/** « Pièce 3 » : le premier numéro libre, pas le nombre de zones. */
export function libelleSuivant(zones) {
  const pris = new Set(zones.filter((z) => z.type === 'piece').map((z) => z.libelle));
  let n = 1;
  while (pris.has(`Pièce ${n}`)) n += 1;
  return `Pièce ${n}`;
}

/** Ajoute une zone tracée. Un second cartouche REMPLACE le premier. */
export function ajouterZone(zones, zone, type) {
  if (type === 'cartouche') {
    return [...zones.filter((z) => z.type !== 'cartouche'), { ...zone, type: 'cartouche', libelle: null }];
  }
  return [...zones, { ...zone, type: 'piece', libelle: libelleSuivant(zones) }];
}

/** Les zones rendues par le serveur (`decoupe_zone`) → l'état de l'éditeur. */
export function zonesDepuisServeur(enregistrees = []) {
  const zones = enregistrees.map((z) => ({ ...z.piece, type: 'piece', libelle: z.libelle }));
  const cartouche = enregistrees.find((z) => z.cartouche)?.cartouche;
  return cartouche ? [...zones, { ...cartouche, type: 'cartouche', libelle: null }] : zones;
}

/** Le rang de couleur d'une zone pièce : 1 à 6, dans l'ordre de tracé. */
export function rangCouleur(zones, index) {
  const avant = zones.slice(0, index).filter((z) => z.type === 'piece').length;
  return (avant % 6) + 1;
}

/** Ce que la découpe va faire, en phrases — le résumé avant validation. */
export function resumeDecoupe(zones, pieces = []) {
  const n = zones.filter((z) => z.type === 'piece').length;
  const cartouche = zones.some((z) => z.type === 'cartouche');
  const lignes = [
    `${n} pièce${n > 1 ? 's' : ''} ${n > 1 ? 'seront créées' : 'sera créée'}, chacune lue comme une page déposée.`,
    cartouche
      ? 'Le cartouche est joint sous chaque pièce.'
      : 'Aucun cartouche joint : la matière et la référence ne seront lues que si elles sont dans la zone.',
  ];
  for (const p of pieces) {
    lignes.push(`La pièce « ${p.nom ?? `n° ${p.id}`} » est retirée du devis et de son total.`);
  }
  return lignes;
}
